import type {
  Section,
  WebSection,
  WebSectionId,
  WebSectionLeft,
  WebSectionRight,
} from "./types";

/**
 * Groups half-width sections across intervening full-width sections.
 * A trailing unpaired section remains a one-item row, without a visual spacer.
 */
export const groupWebSectionsIntoRows = (
  sections: WebSection[],
  getSectionSize: (sectionId: string) => number,
): (WebSection | WebSection[])[] => {
  const rows: (WebSection | WebSection[])[] = [];

  sections.forEach((section) => {
    if (section.section_id === "blank") return;
    if (getSectionSize(section.section_id) === 2) {
      rows.push(section);
      return;
    }
    // Find the first half row missing a pair and insert the next half row we find
    const halfRow = rows.find(
      (row): row is WebSection[] => Array.isArray(row) && row.length === 1,
    );
    if (halfRow) {
      halfRow.push(section);
    } else {
      rows.push([section]);
    }
  });

  return rows;
};

/**
 * Merges a user's customized dashboard layout with an FI specified map
 * of sections and indexes.
 */
export const applyPositionOverrides = <T extends string>(
  list: T[],
  overrides: Partial<Record<T, number>>,
): T[] => {
  const result = [...list];
  (Object.entries(overrides) as [string, number][]).forEach(
    ([name, newIndex]) => {
      const currentIndex = result.indexOf(name as T);
      if (currentIndex > -1) {
        result.splice(currentIndex, 1);
      }
      result.splice(newIndex, 0, name as T);
    },
  );
  return result;
};

/**
 * Merges a user's customized dashboard layout with an FI specified map
 * of sections and row indexes. Consecutive half-width overrides share a row.
 * An odd number of half-width sections always inserts the last half-width section
 * last to prevent any empty spots in the layout.
 */
export const applyWebSectionPositionOverrides = (
  list: WebSection[],
  overrides: Partial<Record<WebSectionId, number>>,
  getSectionSize: (sectionId: string) => number,
): WebSection[] => {
  // Sort the overrides by provided order number
  const overrideEntries = (
    Object.entries(overrides) as [WebSectionId, number][]
  ).sort(([, left], [, right]) => left - right);
  if (overrideEntries.length === 0) return [...list];

  // Format rows which aren't overridden
  const overriddenIds = new Set(overrideEntries.map(([name]) => name));
  const rows = groupWebSectionsIntoRows(
    list.filter(({ section_id }) => !overriddenIds.has(section_id)),
    getSectionSize,
  );

  // Convert overrideEntries into rows compatible format
  const overrideRows: { index: number; sections: WebSection[] }[] = [];
  for (let i = 0; i < overrideEntries.length; i += 1) {
    const [name, rowIndex] = overrideEntries[i];
    const nextOverride = overrideEntries[i + 1];
    const sections = [{ section_id: name }];

    // If the current section is a half width section and the next item is also half width,
    // we've found a pair and can combine them into a single entry as a row.
    if (
      getSectionSize(name) === 1 &&
      nextOverride &&
      nextOverride[1] === rowIndex + 1 &&
      getSectionSize(nextOverride[0]) === 1
    ) {
      sections.push({ section_id: nextOverride[0] });
      i += 1;
    }

    // If the current section isn't a half width section or the next item isn't a half width section we
    // simply treat it as a single row.
    overrideRows.push({ index: rowIndex, sections });
  }

  // An unmatched half row section will always be inserted last
  const unpairedIndex = overrideRows.some(
    ({ sections }) => sections.length === 2,
  )
    ? rows.findIndex((row) => Array.isArray(row) && row.length === 1)
    : -1;
  const unpairedRows =
    unpairedIndex === -1 ? [] : rows.splice(unpairedIndex, 1);

  // Insert overrides into rows and place hanging half row section last
  overrideRows.forEach(({ index, sections }) => {
    rows.splice(index, 0, sections);
  });
  return [...rows, ...unpairedRows].flat();
};

/**
 * Updates a user's customized section order (mobile, and left or right web columns)
 * with all sections that should or should not be shown.
 */
export const enforceVisibleSections = <T extends Section>(
  userSectionOrder: T[],
  _allVisibleSections: T[],
): T[] => {
  const visibleSections: T[] = [];
  const remainingSections = [..._allVisibleSections];

  userSectionOrder.forEach((userSection) => {
    if (remainingSections.includes(userSection)) {
      visibleSections.push(userSection);
      remainingSections.splice(remainingSections.indexOf(userSection), 1);
    }
  });

  visibleSections.push(...remainingSections);

  return [...new Set(visibleSections)];
};

export const filterVisibleSections = (
  sections: WebSection[],
  visibleSectionIds: WebSectionId[],
): WebSection[] => {
  const visibleSections = sections.filter((section) =>
    visibleSectionIds.includes(section.section_id),
  );

  const seen = new Set<string>(
    visibleSections.map((section) => section.section_id),
  );

  const missingVisibleSections = visibleSectionIds
    .filter((sectionId) => !seen.has(sectionId))
    .map((section_id) => ({ section_id }));

  return [...visibleSections, ...missingVisibleSections];
};

export const convertToWebSection = (
  webSection: WebSectionLeft | WebSectionRight,
): WebSection => {
  return {
    section_id: webSection,
  };
};
